
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import React from 'react';
import { Completion } from 'types';
const Steps = dynamic(() => import('@/components/atoms/Steps'));
const CallToAction = dynamic(() => import('@/components/CallToAction'));
const Footer = dynamic(() => import('@/components/layout/Footer'));
const Seo = dynamic(() => import('@/components/Seo'));
const InteriorHeroSection = dynamic(() => import('@/components/landing/RoomGPT/InteriorHeroSection'));
// load Testimonials component only on client side
const Testimonials = dynamic(() => import('@/components/Testimonials'), {
    ssr: false,
});
const FaqTemplate = dynamic(() => import('@/components/FaqTemplate'));
import useLocale from 'next-translate/useTranslation';
import { getPublicCompletions } from '@/data/completions';
import { RoomThemes } from '@/components/landing/RoomGPT/RoomThemes';
import { RoomTypes } from '@/components/landing/RoomGPT/RoomTypes';
import { TakePhotoSection } from '@/components/landing/RoomGPT/TakePhotoSection';
import { ThreeDFlythrough } from '@/components/landing/RoomGPT/ThreeDFlythrough';
import { Preset, PresetCategory, presets } from '@/constants/presets';
import { MultiTurnEditingSection } from '@/components/landing/RoomGPT/MultiTurnEditingSection';
import { SketchToRenderSection } from '@/components/landing/RoomGPT/SketchToRenderSection';
interface TemplateProps {
    completions: Completion[];
    preset: Preset;
    locale?: string;
    otherPresets: Preset[];
}
const Template = ({ completions = [], preset, locale = 'en', otherPresets, }: TemplateProps) => {
    // const router = useRouter();
    // const { slug } = router.query;
    const { t, lang } = useLocale('tools');
    // const preset = presets.find((preset) => preset.slug === slug);
    if (!preset)
        return null;
    return (<main className='bg-gray-100 dark:bg-slate-900'>
      {/* <Banner /> */}
      <Seo templateTitle={lang === 'en'
            ? `AI Interior Design Generator, Virtual Staging AI tool + Room GPT AI`
            : t(preset.title)} description={lang === 'en'
            ? `${t(preset.description)}${preset.isFree ? ' Use it for Free.' : ''}`
            : t(preset.description)} image={`https://easy-peasy.ai/api/og-template?title=${t(preset.title)}&description=${t(preset.description)}`}/>
      <div className='relative overflow-hidden bg-white dark:bg-slate-900'>
        <div className='mx-auto'>
          <div className='relative z-10 bg-white dark:bg-slate-900 lg:w-full'>
            <InteriorHeroSection preset={preset}/>
          </div>
        </div>
      </div>
      <TakePhotoSection />
      <ThreeDFlythrough />
      <SketchToRenderSection />
      <MultiTurnEditingSection />
      <section className='bg-white dark:bg-slate-800'>
        <div className='mx-auto max-w-md px-4 py-5 text-center sm:max-w-3xl sm:px-6 sm:py-10 lg:max-w-7xl lg:px-8'>
          <p className='mt-2 text-3xl font-bold tracking-tight text-primary-600 sm:text-4xl'>
            {t(`It's easy to get started`)}
          </p>
          {/* <p className='mx-auto mt-5 max-w-prose text-xl text-gray-500'>
          With AI's help, you can create copy that is perfectly tailored to
          your audience and your message, so you can stay ahead of the
          competition.
        </p> */}
        </div>
        <div className='flex justify-center px-5 pt-10'>
          <Steps preset={preset}/>
        </div>
      </section>
      <RoomThemes />
      <RoomTypes />
      {lang === 'en' && <FaqTemplate />}
      <Testimonials />
      {/* <ContactUs /> */}
      <CallToAction />
      <Footer />
    </main>);
};
export default Template;
const getStaticProps = async (ctx: GetStaticPropsContext) => {
    const locale = ctx.locale as string;
    // map es to Spanish, ja to Japanese
    let lang;
    switch (locale) {
        case 'es':
            lang = 'Spanish';
            break;
        case 'ja':
            lang = 'Japanese';
            break;
        case 'fr':
            lang = 'French';
            break;
        case 'it':
            lang = 'Italian';
            break;
        case 'de':
            lang = 'German';
            break;
        case 'ko':
            lang = 'Korean';
            break;
        case 'zh':
            lang = 'Chinese';
            break;
        case 'th':
            lang = 'Thai';
            break;
        case 'ar':
            lang = 'Arabic';
            break;
        case 'pt':
            lang = 'Portuguese';
            break;
        case 'ms':
            lang = 'Malay';
            break;
        default:
            lang = '';
    }
    console.log('lang', lang, locale);
    const completions = await getPublicCompletions('ai-interior-designer', lang);
    const preset = presets.find((preset) => preset.slug === 'ai-interior-designer');
    if (!preset) {
        return { notFound: true };
    }
    // do not include icon from preset, exclude it from the preset
    const { icon, ...presetWithoutIcon } = preset;
    const templateTitleWords = presetWithoutIcon.title
        .replace('Generator', '')
        .trim()
        .split(' ');
    const otherPresets = presets
        .filter((p) => (p.category === preset.category ||
        p.category.includes(p.category[0] as PresetCategory)) &&
        p.slug !== preset.slug &&
        !p.hideLandingPage)
        .map((preset) => {
        const { icon, ...presetWithoutIcon } = preset;
        return presetWithoutIcon;
    });
    const presetsWithTitleMatch = otherPresets
        .filter((preset) => templateTitleWords.some((word) => preset.title.includes(word)))
        .slice(0, 12);
    const remainingPresets = otherPresets
        .filter((preset) => !presetsWithTitleMatch.includes(preset))
        .slice(0, 12 - presetsWithTitleMatch.length);
    const finalPresets = [...presetsWithTitleMatch, ...remainingPresets];
    return {
        props: {
            completions,
            preset: presetWithoutIcon,
            locale,
            otherPresets: finalPresets,
        },
    };
};

    async function __Next_Translate__getStaticProps__1a0cbfb66b2__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-interior-design-generator/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb66b2__ as getStaticProps }
  